import { createClient } from "@supabase/supabase-js";

import { createMemoryStorage, isLocalStorageAvailable } from "@/lib/clientStorage";

const supabaseUrl     = import.meta.env.VITE_SUPABASE_URL;
const supabaseAnonKey = import.meta.env.VITE_SUPABASE_ANON_KEY;

if (!supabaseUrl || !supabaseAnonKey) {
  throw new Error("Faltan las variables de entorno de Supabase en el archivo .env.local");
}

// ─────────────────────────────────────────────
// SESSION PERSISTENCE CONTRACT
// ─────────────────────────────────────────────
//
// Supabase v2 supports exactly one `storage` adapter for the lifetime of
// the client — you cannot swap to `sessionStorage` on a per-login basis.
// The client is therefore permanently pinned to `localStorage` so that
// sessions survive tab closures and browser restarts *by default*.
//
// "Keep me signed in" is implemented as a second layer on top:
//
//   • EPHEMERAL_FLAG_KEY (localStorage)  — written when the user
//     UNchecks "Keep me signed in" at login. Survives tab closures
//     because it's a persistent *intent* we need to remember.
//
//   • TAB_ALIVE_KEY       (sessionStorage) — written on every bootstrap
//     of the app. Dies naturally when the tab is closed.
//
// On page load we check both: if the ephemeral flag is set but no
// tab-alive marker is present, we know the tab that set the intent has
// been closed and the stored token must be purged before the Supabase
// client reads it. All of this runs in an IIFE *before* `createClient`,
// so the client constructor never sees the stale session.
//
// Known limitation: opening a second tab of the app while the first is
// still open, in ephemeral mode, will also wipe the session. Users who
// opted out of "remember me" on a shared machine accept that trade-off.

const EPHEMERAL_FLAG_KEY  = "orf.auth.ephemeral";
const TAB_ALIVE_KEY       = "orf.auth.tabAlive";

/**
 * Supabase v2's default storageKey format — derived from the project ref
 * (the subdomain of the Supabase URL). We recompute it locally so we can
 * evict the token synchronously *before* `createClient` runs.
 */
const SUPABASE_STORAGE_KEY =
  `sb-${new URL(supabaseUrl).hostname.split(".")[0]}-auth-token`;

function readLocal(key: string): string | null {
  try { return localStorage.getItem(key); } catch { return null; }
}
function writeLocal(key: string, value: string): void {
  try { localStorage.setItem(key, value); } catch { /* ignore */ }
}
function removeLocal(key: string): void {
  try { localStorage.removeItem(key); } catch { /* ignore */ }
}
function readSession(key: string): string | null {
  try { return sessionStorage.getItem(key); } catch { return null; }
}
function writeSession(key: string, value: string): void {
  try { sessionStorage.setItem(key, value); } catch { /* ignore */ }
}

// ── Enforce the ephemeral-session contract BEFORE createClient runs ──
// createClient reads from storage during construction and caches the
// session in memory. If we purged the token *after* construction the
// client would still believe it was signed in.
(function enforceEphemeralSessionPolicy() {
  const ephemeral = readLocal(EPHEMERAL_FLAG_KEY) === "1";
  const tabAlive  = readSession(TAB_ALIVE_KEY)    === "1";

  if (ephemeral && !tabAlive) {
    // Prior tab set ephemeral intent and has since been closed.
    // Evict the Supabase token + the flag itself so the client boots
    // into a clean "signed out" state.
    removeLocal(SUPABASE_STORAGE_KEY);
    removeLocal(EPHEMERAL_FLAG_KEY);
  }

  // Mark *this* tab alive so subsequent in-tab navigations (e.g. SPA
  // route changes, pull-to-refresh) don't trip the policy above.
  writeSession(TAB_ALIVE_KEY, "1");
})();

import { fetchWithTimeout } from "@/lib/fetchWithTimeout";

/** False when the browser blocks localStorage — auth falls back to in-memory (tab-only). */
export const isAuthStoragePersistent = isLocalStorageAvailable();

const authStorage = isAuthStoragePersistent
  ? window.localStorage
  : createMemoryStorage();

export const supabase = createClient(supabaseUrl, supabaseAnonKey, {
  auth: {
    storage:            authStorage,
    storageKey:         SUPABASE_STORAGE_KEY,
    persistSession:     true,
    autoRefreshToken:   true,
    detectSessionInUrl: true,
    experimental:       { passkey: true },
  },
  global: {
    fetch: (input, init) => fetchWithTimeout(input, init),
  },
});

// ─────────────────────────────────────────────
// PUBLIC PERSISTENCE API
// ─────────────────────────────────────────────

/**
 * Record the user's "Keep me signed in" intent. Call this once, right
 * after a successful `signInWithPassword`, before navigating away.
 *
 * @param keepSignedIn — `true` when the checkbox is ticked. Persists
 *   across tab closures and browser restarts (the normal Supabase
 *   default). `false` marks the session as ephemeral — it will be
 *   purged on the next app bootstrap in a fresh tab.
 */
export function setSessionPersistence(keepSignedIn: boolean): void {
  if (keepSignedIn) {
    removeLocal(EPHEMERAL_FLAG_KEY);
  } else {
    writeLocal(EPHEMERAL_FLAG_KEY, "1");
  }
  // Refresh the tab-alive marker so in-app route changes don't
  // prematurely trigger the ephemeral policy.
  writeSession(TAB_ALIVE_KEY, "1");
}

/**
 * Clear every persistence flag this module owns. Safe to call at sign-out
 * alongside `supabase.auth.signOut()` — guarantees a clean slate for the
 * next login on the same device.
 */
export function clearSessionPersistenceFlags(): void {
  removeLocal(EPHEMERAL_FLAG_KEY);
  try { sessionStorage.removeItem(TAB_ALIVE_KEY); } catch { /* ignore */ }
}
